import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import SourceBranchIcon from 'mdi-react/SourceBranchIcon';
import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import {
  useCreateBranchMutation,
  useDeleteBranchMutation,
  useListBranchesQuery,
  useListChannelsQuery,
  usePatchChannelMutation,
} from 'store/app/entities/OtaSlice';
import { I18n } from 'utils/i18n';
import RuntimeLinesTable from './RuntimeLinesTable';

const BRANCH_NAME_RE = /^[a-z0-9._-]+$/i;

const GridToolbarNone = () => null;
const GridFooterNone = () => null;

const getBranchTypeChipSx = theme => ({
  bgcolor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.dark,
  fontWeight: 600,
  border: 'none',
  alignSelf: 'flex-start',
});

const OtaChannelDetailPage = () => {
  const { channelName } = useParams();
  const decodedName = decodeURIComponent(channelName || '');
  const { data, isLoading, error } = useListChannelsQuery();
  const { data: branchData, isFetching: isFetchingBranches } = useListBranchesQuery(decodedName, {
    skip: !decodedName,
  });
  const [patchChannel] = usePatchChannelMutation();
  const [createBranch, { isLoading: isCreatingBranch }] = useCreateBranchMutation();
  const [deleteBranch] = useDeleteBranchMutation();
  const [createBranchOpen, setCreateBranchOpen] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [branchError, setBranchError] = useState('');

  const channel = useMemo(
    () => data?.channels?.find(c => c.name === decodedName),
    [data, decodedName],
  );
  const branches = branchData?.branches || [];
  const runtimeLines = channel?.runtimeLines || [];

  const onSetDefaultBranch = async branchName => {
    if (!channel) return;
    await patchChannel({ name: channel.name, defaultBranch: branchName }).unwrap();
  };

  const closeCreateBranchDialog = () => {
    setCreateBranchOpen(false);
    setNewBranchName('');
    setBranchError('');
  };

  const onCreateBranch = async () => {
    setBranchError('');
    const n = newBranchName.trim();
    if (!BRANCH_NAME_RE.test(n)) {
      setBranchError(I18n.t('AdminOtaScene.Branches.invalidName'));
      return;
    }
    try {
      await createBranch({ channelName: decodedName, name: n }).unwrap();
      closeCreateBranchDialog();
    } catch (e) {
      setBranchError(e?.data?.error || String(e));
    }
  };

  const onDeleteBranch = async bn => {
    if (!window.confirm(I18n.t('AdminOtaScene.Branches.confirmDelete', { name: bn }))) return;
    try {
      await deleteBranch({ channelName: decodedName, branchName: bn }).unwrap();
    } catch (e) {
      window.alert(e?.data?.error || String(e));
    }
  };

  if (isLoading) return null;
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          {I18n.t('AdminOtaScene.loadError')}:{' '}
          {error?.data?.error || error?.message || String(error)}
        </Typography>
      </Box>
    );
  }
  if (!channel) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ mb: 2 }}>{I18n.t('AdminOtaScene.ChannelDetail.notFound')}</Typography>
        <Button component={RouterLink} to="/home/admin/ota/channels" variant="outlined">
          {I18n.t('AdminOtaScene.nav.backChannels')}
        </Button>
      </Box>
    );
  }

  const branchColumns = [
    {
      field: 'name',
      flex: 1.4,
      minWidth: 220,
      editable: false,
      sortable: false,
      headerName: I18n.t('AdminOtaScene.Branches.colName'),
      renderCell: params => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: 'primary.ultraLight',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
            }}
          >
            <SvgIcon sx={{ fontSize: 16 }}>
              <SourceBranchIcon />
            </SvgIcon>
          </Box>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }} noWrap>
            {params.row.name}
          </Typography>
          {params.row.name === channel.defaultBranch && (
            <Chip
              size="small"
              label={I18n.t('AdminOtaScene.Branches.default')}
              sx={getBranchTypeChipSx}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      flex: 0.7,
      minWidth: 200,
      editable: false,
      sortable: false,
      headerName: '',
      align: 'right',
      headerAlign: 'right',
      renderCell: params => {
        const isDefault = params.row.name === channel.defaultBranch;
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant="text"
              color="primary"
              disabled={isDefault}
              onClick={() => onSetDefaultBranch(params.row.name)}
            >
              {I18n.t('AdminOtaScene.Branches.setDefault')}
            </Button>
            <IconButton
              size="small"
              disabled={isDefault}
              onClick={() => onDeleteBranch(params.row.name)}
              title={I18n.t('AdminOtaScene.Branches.delete')}
            >
              <SvgIcon fontSize="small">
                <DeleteOutlineIcon />
              </SvgIcon>
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button
          component={RouterLink}
          to="/home/admin/ota/channels"
          variant="text"
          color="secondary"
          size="small"
          sx={{ ml: -1 }}
        >
          <SvgIcon sx={{ fontSize: 18, mr: 0.5 }}>
            <ArrowLeftIcon />
          </SvgIcon>
          {I18n.t('AdminOtaScene.nav.backChannels')}
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h3">{channel.name}</Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          {I18n.t('AdminOtaScene.ChannelDetail.defaultBranch')}:{' '}
          <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {channel.defaultBranch}
          </Box>
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5">{I18n.t('AdminOtaScene.Branches.title')}</Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            {I18n.t('AdminOtaScene.Branches.description')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          sx={{ flexShrink: 0 }}
          onClick={() => {
            setBranchError('');
            setNewBranchName('');
            setCreateBranchOpen(true);
          }}
        >
          <SvgIcon sx={{ fontSize: 18, mr: 0.5 }}>
            <PlusIcon />
          </SvgIcon>
          {I18n.t('AdminOtaScene.Branches.create')}
        </Button>
      </Box>

      <Box sx={{ minHeight: 120, mb: 5 }}>
        <OmmnioDataGrid
          loading={isFetchingBranches}
          columns={branchColumns}
          rows={branches}
          getRowId={r => r._id}
          disableEvenOdd
          pagination={false}
          hideFooter
          slots={{ toolbar: GridToolbarNone, footer: GridFooterNone }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h5">{I18n.t('AdminOtaScene.RuntimeLines.title')}</Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          {I18n.t('AdminOtaScene.RuntimeLines.description')}
        </Typography>
      </Box>
      {runtimeLines.length === 0 ? (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {I18n.t('AdminOtaScene.noRuntimeLines')}
          </Typography>
        </Box>
      ) : (
        <RuntimeLinesTable channelName={decodedName} lines={runtimeLines} />
      )}

      <Dialog open={createBranchOpen} onClose={closeCreateBranchDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{I18n.t('AdminOtaScene.Branches.create')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={I18n.t('AdminOtaScene.Branches.newName')}
            value={newBranchName}
            onChange={e => setNewBranchName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onCreateBranch();
              }
            }}
            error={Boolean(branchError)}
            helperText={branchError}
            disabled={isCreatingBranch}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateBranchDialog} disabled={isCreatingBranch}>
            {I18n.t('AdminOtaScene.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={onCreateBranch}
            disabled={isCreatingBranch || !newBranchName.trim()}
          >
            {I18n.t('AdminOtaScene.Branches.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OtaChannelDetailPage;
